/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { mergeCdsDefaultMessages, mergeCdsDefaultMessagesForLocale } from './util/mergeCdsDefaultMessages';

const buildEagerLocaleMessages = eagerMessages => mergeCdsDefaultMessages(eagerMessages || {}) || {};

export const useLazyLocaleMessages = ({ messages: eagerMessages, messagesLoaders, isLazyMode }) => {
  const [localeMessages, setLocaleMessages] = useState(() => buildEagerLocaleMessages(eagerMessages));
  const [loadingLocale, setLoadingLocale] = useState(null);
  const loadedLocalesRef = useRef(new Set(Object.keys(eagerMessages || {})));
  const inFlightRef = useRef({});
  const isFirstEagerSyncRef = useRef(true);

  useEffect(() => {
    if (!isLazyMode) {
      return;
    }

    if (isFirstEagerSyncRef.current) {
      isFirstEagerSyncRef.current = false;

      return;
    }

    const eagerWithDefaults = buildEagerLocaleMessages(eagerMessages);

    Object.keys(eagerWithDefaults).forEach(locale => {
      loadedLocalesRef.current.add(locale);
    });

    setLocaleMessages(prev => ({ ...prev, ...eagerWithDefaults }));
  }, [eagerMessages, isLazyMode]);

  const ensureLocaleLoaded = useCallback(
    async locale => {
      if (!isLazyMode) {
        return true;
      }

      if (!locale) {
        return false;
      }

      if (loadedLocalesRef.current.has(locale)) {
        return true;
      }

      if (eagerMessages?.[locale]) {
        const merged = mergeCdsDefaultMessagesForLocale(locale, eagerMessages[locale]);

        loadedLocalesRef.current.add(locale);
        setLocaleMessages(prev => ({ ...prev, [locale]: merged }));

        return true;
      }

      const loader = messagesLoaders?.[locale];

      if (!loader) {
        console.warn(`No messages loader for locale "${locale}"`);

        return false;
      }

      if (inFlightRef.current[locale]) {
        return inFlightRef.current[locale];
      }

      setLoadingLocale(locale);

      const loadPromise = (async () => {
        try {
          const rawMessages = await loader();
          const merged = mergeCdsDefaultMessagesForLocale(locale, rawMessages);

          loadedLocalesRef.current.add(locale);
          setLocaleMessages(prev => ({ ...prev, [locale]: merged }));

          return true;
        } catch (error) {
          console.error(`Failed to load messages for locale "${locale}"`, error);

          return false;
        } finally {
          setLoadingLocale(current => (current === locale ? null : current));
          delete inFlightRef.current[locale];
        }
      })();

      inFlightRef.current[locale] = loadPromise;

      return loadPromise;
    },
    [eagerMessages, isLazyMode, messagesLoaders]
  );

  const seedCdsOnlyLocale = useCallback(locale => {
    if (!locale) {
      return;
    }

    const merged = mergeCdsDefaultMessagesForLocale(locale, {});

    loadedLocalesRef.current.add(locale);
    setLocaleMessages(prev => ({ ...prev, [locale]: merged }));
  }, []);

  return {
    localeMessages,
    loadingLocale,
    ensureLocaleLoaded,
    seedCdsOnlyLocale,
  };
};
